import React, { useState, useEffect } from 'react';
import { Database, Server, Clock, RefreshCw, AlertCircle, CheckCircle, Search, Ban, HardDrive, Trash2, Activity, Eye, Sparkles, Save } from 'lucide-react';
import axios from 'axios';
import { ConfirmationModal } from '../ui/ConfirmationModal';

const RagStatusTab = () => {
    const [stats, setStats] = useState({ count: 0, lastSync: 'Loading...', status: 'Unknown', estimatedSize: '0 KB' });
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isInspectorOpen, setIsInspectorOpen] = useState(false);
    const [inspectorItems, setInspectorItems] = useState([]);
    const [loadingInspect, setLoadingInspect] = useState(false);
    const [error, setError] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [showSyncConfirm, setShowSyncConfirm] = useState(false);

    // Review Modal State
    const [reviewItem, setReviewItem] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [aiSuggestion, setAiSuggestion] = useState(null);
    const [manualDescription, setManualDescription] = useState("");
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isSavingManual, setIsSavingManual] = useState(false);

    const fetchStats = React.useCallback(async () => {
        try {
            const res = await fetch('/api/ai/stats');
            const data = await res.json();
            if (data.success) {
                setStats({
                    count: data.count,
                    lastSync: data.lastSync === 'Never' ? 'Never' : new Date(data.lastSync).toLocaleString(),
                    status: data.status,
                    estimatedSize: data.estimatedSize,
                    isSyncing: data.isSyncing,
                    progress: data.progress,
                    workerStatus: data.workerStatus,
                    lastWorkerActivity: data.lastWorkerActivity,
                    jobStats: data.jobStats // Granular stats
                });

                if (data.isSyncing) setIsSyncing(true);
                else if (isSyncing && !data.isSyncing) setIsSyncing(false);
            }
        } catch (err) {
            console.error("Failed to fetch RAG stats", err);
        }
    }, [isSyncing]);

    useEffect(() => {
        setIsLoading(true);
        fetchStats().then(() => setIsLoading(false));
    }, [fetchStats]);

    const [logs, setLogs] = useState([]);

    const fetchLogs = React.useCallback(async () => {
        try {
            const res = await fetch('/api/ai/rag/logs');
            const data = await res.json();
            if (data.success) {
                // If logs are same (by length/last entry), avoid jitter?
                // Just setLogs logic is fine, React diffs efficiently.
                setLogs(data.logs || []);
            }
        } catch (e) { console.error("Log fetch failed", e); }
    }, []);

    const handleSyncClick = () => {
        setShowSyncConfirm(true);
    };

    const confirmSync = async () => {
        setIsSyncing(true);
        setError(null);
        setShowSyncConfirm(false);

        try {
            const res = await fetch('/api/ai/sync', { method: 'POST' });
            const result = await res.json();

            if (!result.success && res.status !== 409) {
                setError("Sync failed: " + result.error);
                setIsSyncing(false);
            } else {
                fetchStats();
            }
        } catch {
            setError("Sync request failed.");
            setIsSyncing(false);
        }
    };

    const confirmIncrementalSync = async () => {
        setIsSyncing(true);
        setError(null);

        try {
            const res = await fetch('/api/ai/sync/incremental', { method: 'POST' });
            const result = await res.json();

            if (!result.success && res.status !== 409) {
                setError("Incremental Sync failed: " + result.error);
                setIsSyncing(false);
            } else {
                fetchStats();
            }
        } catch {
            setError("Incremental Sync request failed.");
            setIsSyncing(false);
        }
    };

    useEffect(() => {
        let interval;
        if (isSyncing || (stats.jobStats && (stats.jobStats.PENDING > 0 || stats.jobStats.RUNNING > 0))) {
            const poll = () => { fetchStats(); fetchLogs(); };
            interval = setInterval(poll, 2000);
            poll(); // Immediate fetch on state change
        }
        return () => clearInterval(interval);
    }, [isSyncing, stats.jobStats, fetchStats, fetchLogs]);

    const handleInspect = async (statusOverride = null) => {
        setIsInspectorOpen(true);
        setLoadingInspect(true);
        setFilterStatus(statusOverride);
        try {
            const url = statusOverride
                ? `/api/ai/rag/inspect?limit=50&status=${statusOverride}`
                : `/api/ai/rag/inspect?limit=50`;
            const res = await fetch(url);
            const data = await res.json();
            if (data.success) setInspectorItems(data.items);
        } catch (err) {
            console.error("Failed to inspect", err);
            setError("Failed to load inspector data");
        } finally {
            setLoadingInspect(false);
        }
    };

    const handleStop = async () => {
        try {
            const res = await fetch('/api/ai/rag/stop', { method: 'POST' });
            const result = await res.json();
            if (result.success) {
                setIsSyncing(false);
                fetchStats();
            } else setError("Stop failed: " + result.error);
        } catch (err) {
            setError("Failed to stop sync.");
        }
    };

    const handleClearFailedClick = () => setShowClearConfirm(true);

    const confirmClearFailed = async () => {
        setLoadingInspect(true);
        try {
            await fetch('/api/ai/rag/jobs/failed', { method: 'DELETE' });
            fetchStats();
            handleInspect('FAILED');
        } catch (err) {
            console.error("Failed to clear", err);
        } finally {
            setLoadingInspect(false);
            setShowClearConfirm(false);
        }
    };

    const handleReview = (item) => {
        setReviewItem(item);
        setAiSuggestion(null);
        setManualDescription("");
        setShowReviewModal(true);
    };

    const analyzeImage = async () => {
        if (!reviewItem || !reviewItem.metadata?.file_path) return;
        setIsAnalyzing(true);
        try {
            const res = await axios.post('/api/ai/analyze-image', { imagePath: reviewItem.metadata.file_path });
            if (res.data.success) {
                setAiSuggestion(res.data.suggestion);
            } else {
                setAiSuggestion("Error: " + res.data.error);
            }
        } catch (err) {
            setAiSuggestion("Connection failed.");
        } finally {
            setIsAnalyzing(false);
        }
    };

    const saveManualIngest = async () => {
        if (!manualDescription || !reviewItem) return;
        setIsSavingManual(true);
        try {
            await axios.post('/api/ai/rag/manual-ingest', {
                documentId: reviewItem.metadata.doc_id, // Ensure doc_id is available in metadata
                text: manualDescription,
                metadata: reviewItem.metadata
            });
            setShowReviewModal(false);
            // Optionally refresh stats or move item
            fetchStats();
        } catch (err) {
            console.error(err);
            alert("Failed to save: " + err.message);
        } finally {
            setIsSavingManual(false);
        }
    };

    const handleRetry = async (jobId) => {
        const numericId = jobId.toString().replace(/[^0-9]/g, '');
        try {
            const res = await fetch(`/api/ai/rag/jobs/${numericId}/retry`, { method: 'POST' });
            const result = await res.json();
            if (result.success) {
                handleInspect(filterStatus);
                fetchStats();
            }
        } catch (err) { console.error("Retry network error", err); }
    };

    // Calculation Logic
    const calculateTotal = () => {
        if (!stats.jobStats) return 1;
        return (stats.jobStats.PENDING || 0) + (stats.jobStats.RUNNING || 0) + (stats.jobStats.COMPLETED || 0) + (stats.jobStats.FAILED || 0);
    };
    const totalJobs = calculateTotal();

    // Prefer backend granular progress (float) if available, else fallback to job count
    const rawProgress = stats.progressPercent !== undefined ? stats.progressPercent : (((stats.jobStats?.COMPLETED || 0) + (stats.jobStats?.FAILED || 0)) / (totalJobs || 1)) * 100;
    const progressDisplay = Math.min(100, Math.max(0, rawProgress)).toFixed(3); // 3 decimals


    return (
        <div className="p-6 text-stone-200 font-sans h-full overflow-y-auto w-full">
            <style>{`
                @keyframes progress-stripes {
                    0% { background-position: 1rem 0; }
                    100% { background-position: 0 0; }
                }
                .animate-stripes {
                    background-image: linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent);
                    background-size: 1rem 1rem;
                    animation: progress-stripes 1s linear infinite;
                }
            `}</style>

            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Database className="text-purple-400" /> Knowledge Base & Vector Store
            </h2>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Document Count */}
                <div className="bg-stone-900/50 border border-stone-800 p-6 rounded-xl flex items-center gap-4 shadow-lg hover:border-purple-500/30 transition-colors backdrop-blur-sm">
                    <div className="w-12 h-12 rounded-full bg-purple-900/20 flex items-center justify-center text-purple-400">
                        <Database size={24} />
                    </div>
                    <div>
                        <div className="text-stone-500 text-xs font-semibold uppercase tracking-wider">Total Chunks</div>
                        <div className="text-3xl font-bold text-white mt-1">{stats.count}</div>
                    </div>
                </div>

                {/* Estimated Size */}
                <div className="bg-stone-900/50 border border-stone-800 p-6 rounded-xl flex items-center gap-4 shadow-lg hover:border-blue-500/30 transition-colors backdrop-blur-sm">
                    <div className="w-12 h-12 rounded-full bg-blue-900/20 flex items-center justify-center text-blue-400">
                        <HardDrive size={24} />
                    </div>
                    <div>
                        <div className="text-stone-500 text-xs font-semibold uppercase tracking-wider">Estimated Size</div>
                        <div className="text-3xl font-bold text-white mt-1">{stats.estimatedSize}</div>
                    </div>
                </div>

                {/* Last Sync */}
                <div className="bg-stone-900/50 border border-stone-800 p-6 rounded-xl flex items-center gap-4 shadow-lg hover:border-green-500/30 transition-colors backdrop-blur-sm">
                    <div className="w-12 h-12 rounded-full bg-green-900/20 flex items-center justify-center text-green-400">
                        <Clock size={24} />
                    </div>
                    <div>
                        <div className="text-stone-500 text-xs font-semibold uppercase tracking-wider">Last Synced</div>
                        <div className="text-lg font-bold text-white mt-1 leading-tight">{stats.lastSync}</div>
                    </div>
                </div>
            </div>

            {/* Ghost Panel (Controls) */}
            <div className="bg-stone-900/20 backdrop-blur-md border border-stone-800/50 rounded-2xl p-8 text-center max-w-2xl mx-auto mb-8 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-900/5 to-blue-900/5 pointer-events-none" />

                <h3 className="text-lg font-semibold text-white mb-2 relative z-10">Sync Management</h3>
                <p className="text-stone-400 text-sm mb-6 max-w-md mx-auto relative z-10">
                    Manage the Vector Index status.
                </p>

                {error && (
                    <div className="bg-red-900/20 border border-red-800 text-red-200 p-3 rounded mb-4 text-sm inline-block animate-in fade-in slide-in-from-top-2 relative z-10">
                        {error}
                    </div>
                )}

                <div className="flex justify-center gap-4 relative z-10">
                    <button
                        onClick={handleSyncClick}
                        disabled={isSyncing || isLoading}
                        className={`
                            relative px-8 py-3 rounded-lg font-bold text-white shadow-xl transition-all group overflow-hidden
                            ${isSyncing
                                ? 'bg-stone-800 cursor-not-allowed border border-stone-700'
                                : 'bg-gradient-to-r from-red-600 to-orange-600 hover:scale-105 hover:shadow-orange-500/20'
                            }
                        `}
                    >
                        {/* Button Shine Effect */}
                        {!isSyncing && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] pointer-events-none" />}

                        <div className="flex items-center gap-2">
                            <RefreshCw size={20} className={`transition-transform duration-700 ${isSyncing ? "animate-spin" : "group-hover:rotate-180"}`} />
                            {isSyncing ? "Rebuilding..." : "Rebuild Index"}
                        </div>
                    </button>

                    <button
                        onClick={confirmIncrementalSync}
                        disabled={isSyncing || isLoading}
                        className={`
                            relative px-8 py-3 rounded-lg font-bold text-white shadow-xl transition-all group overflow-hidden
                            ${isSyncing
                                ? 'bg-stone-800 cursor-not-allowed border border-stone-700'
                                : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:scale-105 hover:shadow-emerald-500/20'
                            }
                        `}
                    >
                        {/* Button Shine Effect */}
                        {!isSyncing && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] pointer-events-none" />}

                        <div className="flex items-center gap-2">
                            <Sparkles size={20} className={`transition-transform duration-700 ${isSyncing ? "animate-spin" : "group-hover:rotate-12"}`} />
                            Sync New/Failed
                        </div>
                    </button>

                    {isSyncing && (
                        <button
                            onClick={handleStop}
                            className="px-6 py-3 rounded-lg font-bold text-white bg-red-600/80 hover:bg-red-700 backdrop-blur border border-red-500 transition-all shadow-lg flex items-center gap-2"
                        >
                            <Ban size={20} /> Stop
                        </button>
                    )}

                    <button
                        onClick={() => handleInspect(null)}
                        className="px-6 py-3 rounded-lg font-bold text-white bg-stone-800/60 hover:bg-stone-700 backdrop-blur border border-stone-600 hover:border-stone-500 transition-all shadow-lg flex items-center gap-2"
                    >
                        <Search size={20} />
                        Inspect
                    </button>
                </div>

                {/* Ghost Progress Bar Area */}
                <div className="mt-8 w-full max-w-lg mx-auto relative z-10">
                    {stats.jobStats ? (
                        <div className="space-y-4">

                            {/* Flex Title & Percent */}
                            <div className="flex justify-between items-end px-1">
                                <span className="text-xs font-mono text-stone-500 uppercase tracking-widest flex items-center gap-2">
                                    {stats.jobStats.RUNNING > 0 && <Activity size={12} className="text-blue-400 animate-pulse" />}
                                    Status {stats.chunkStats ? `(${stats.chunkStats.processed} / ${stats.chunkStats.total} chunks)` : ''}
                                </span>
                                <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 font-mono">
                                    {progressDisplay}%
                                </span>
                            </div>

                            {/* Ghost Bar Container */}
                            <div className="h-4 rounded-full bg-stone-900/60 border border-stone-700/50 backdrop-blur p-0.5 flex overflow-hidden shadow-inner">
                                {/* Smooth Progress Bar */}
                                <div style={{ width: `${isNaN(rawProgress) ? 0 : rawProgress}%` }} className="h-full rounded-full bg-gradient-to-r from-blue-600 via-purple-500 to-green-500 shadow-[0_0_15px_rgba(168,85,247,0.4)] transition-all duration-300 relative overflow-hidden">
                                    {/* Striped overlay for activity */}
                                    {(stats.jobStats.RUNNING > 0 || stats.jobStats.PENDING > 0 || isSyncing) && <div className="absolute inset-0 animate-stripes bg-white/20" />}
                                </div>
                            </div>

                            {/* Legend / Counters - Ghost Buttons */}
                            <div className="grid grid-cols-4 gap-2 text-xs text-center font-mono select-none">
                                <div onClick={() => handleInspect('PENDING')} className="bg-stone-800/30 p-2 rounded-lg border border-stone-700/50 text-stone-400 cursor-pointer hover:bg-stone-700/50 transition-all hover:scale-105 active:scale-95">
                                    PENDING<br />
                                    <span className="text-white text-sm font-bold">{stats.jobStats.PENDING}</span>
                                </div>
                                <div onClick={() => handleInspect('RUNNING')} className="bg-blue-900/10 p-2 rounded-lg border border-blue-800/30 text-blue-400 cursor-pointer hover:bg-blue-900/30 transition-all hover:scale-105 active:scale-95">
                                    ACTIVE<br />
                                    <span className="text-white text-sm font-bold">{stats.jobStats.RUNNING}</span>
                                </div>
                                <div onClick={() => handleInspect('COMPLETED')} className="bg-green-900/10 p-2 rounded-lg border border-green-800/30 text-green-400 cursor-pointer hover:bg-green-900/30 transition-all hover:scale-105 active:scale-95">
                                    DONE<br />
                                    <span className="text-white text-sm font-bold">{stats.jobStats.COMPLETED}</span>
                                </div>
                                <div onClick={() => handleInspect('FAILED')} className="bg-red-900/10 p-2 rounded-lg border border-red-800/30 text-red-400 cursor-pointer hover:bg-red-900/30 transition-all hover:scale-105 active:scale-95">
                                    FAIL<br />
                                    <span className="text-white text-sm font-bold">{stats.jobStats.FAILED}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Fallback
                        <div className="w-full bg-stone-800 rounded-full h-2.5 overflow-hidden">
                            <div className="bg-stone-600 h-2.5 w-full"></div>
                        </div>
                    )}


                    <div className="mt-6 flex justify-between items-center text-xs">
                        <div className="font-mono text-purple-300 bg-purple-900/10 px-3 py-1.5 rounded-full border border-purple-800/50 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" />
                            {stats.status || "Initializing..."}
                        </div>

                        {/* Worker Health Indicator & Heartbeat */}
                        {stats.workerStatus && (
                            <div className="flex items-center gap-3">
                                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${stats.workerStatus === 'Active'
                                    ? 'bg-green-900/10 border-green-800/50 text-green-400'
                                    : 'bg-red-900/10 border-red-800/50 text-red-300'
                                    }`}>
                                    <div className={`w-1.5 h-1.5 rounded-full ${stats.workerStatus === 'Active' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                                    <span>Worker: {stats.workerStatus}</span>
                                </div>
                                {stats.lastWorkerActivity && (
                                    <div className="text-stone-500 font-mono text-[10px]">
                                        <Activity size={10} className="inline mr-1" />
                                        Last beat: {Math.floor((new Date() - new Date(stats.lastWorkerActivity)) / 1000)}s ago
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Extraction Phase Indicator - Only if Running but no pending chunks (implied OCR phase) */}
                    {stats.jobStats && stats.jobStats.RUNNING > 0 && stats.chunkStats && stats.chunkStats.pending === 0 && (
                        <div className="mt-4 bg-blue-900/20 border border-blue-800/30 rounded-lg p-3 flex items-center justify-center gap-3">
                            <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                            </span>
                            <span className="text-xs text-blue-200 font-mono">
                                Heavy Processing: Text Extraction & OCR active...
                            </span>
                        </div>
                    )}
                </div>

                {/* Live Terminal Log */}
                {(isSyncing || logs.length > 0) && (
                    <div className="mt-8 mx-auto max-w-2xl bg-black rounded-lg border border-stone-800 font-mono text-xs text-left shadow-2xl overflow-hidden flex flex-col">
                        <div className="bg-stone-900 px-4 py-2 flex justify-between items-center border-b border-stone-800">
                            <span className="text-stone-400 flex items-center gap-2">
                                <Activity size={12} className={stats.jobStats?.RUNNING > 0 ? "animate-pulse text-green-500" : "text-stone-600"} />
                                Live Worker Activity
                            </span>
                            <span className="text-[10px] text-stone-600">tail -f worker.log</span>
                        </div>
                        <div className="h-48 overflow-y-auto p-4 space-y-1 scrollbar-thin scrollbar-thumb-stone-700 scrollbar-track-transparent flex flex-col-reverse">
                            {logs.length === 0 ? (
                                <div className="text-stone-600 italic">Waiting for logs...</div>
                            ) : (
                                logs.map((log, i) => (
                                    <div key={i} className="flex gap-3 text-stone-300 border-b border-stone-900/50 pb-0.5 last:border-0">
                                        <span className="text-stone-600 shrink-0 select-none w-16">
                                            {new Date(log.ts).toLocaleTimeString().split(' ')[0]}
                                        </span>
                                        <span className={`break-words ${log.msg.includes('Error') ? 'text-red-400' : log.msg.includes('Job') ? 'text-blue-300' : 'text-stone-300'}`}>
                                            {log.msg}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Inspector Modal / Section */}
            {
                isInspectorOpen && (
                    <div className="bg-stone-900/90 backdrop-blur border border-stone-800 rounded-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 mb-8">
                        <div className="bg-stone-800/80 px-6 py-4 flex justify-between items-center border-b border-stone-700">
                            <div className="flex items-center gap-4">
                                <h3 className="font-bold text-lg text-white">
                                    {filterStatus ? `Job Inspector (${filterStatus})` : 'Vector Store Inspector'}
                                </h3>
                                {filterStatus === 'FAILED' && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={async () => {
                                                setLoadingInspect(true);
                                                try {
                                                    await fetch('/api/ai/rag/maintenance/prune-missing', { method: 'POST' });
                                                    fetchStats();
                                                    handleInspect('FAILED');
                                                } catch (err) {
                                                    console.error("Failed to prune", err);
                                                } finally {
                                                    setLoadingInspect(false);
                                                }
                                            }}
                                            className="flex items-center gap-2 px-3 py-1 bg-orange-900/30 hover:bg-orange-900/50 text-orange-400 text-xs font-bold border border-orange-800 rounded transition-colors"
                                            title="Removes jobs where the file no longer exists"
                                        >
                                            <Trash2 size={14} /> Prune Missing
                                        </button>
                                        <button
                                            onClick={handleClearFailedClick}
                                            className="flex items-center gap-2 px-3 py-1 bg-red-900/30 hover:bg-red-900/50 text-red-400 text-xs font-bold border border-red-800 rounded transition-colors"
                                        >
                                            <Trash2 size={14} /> Clear All
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button onClick={() => setIsInspectorOpen(false)} className="text-stone-400 hover:text-white bg-stone-800/50 px-3 py-1 rounded hover:bg-stone-700 transition-colors">Close</button>
                        </div>

                        <div className="p-0 overflow-auto max-h-[500px]">
                            {loadingInspect ? (
                                <div className="p-8 text-center text-stone-400 flex flex-col items-center gap-2">
                                    <RefreshCw className="animate-spin text-purple-500" size={24} />
                                    Loading data...
                                </div>
                            ) : (
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-stone-950/50 text-stone-400 text-xs uppercase sticky top-0 backdrop-blur-md">
                                        <tr>
                                            <th className="p-4 font-semibold border-b border-stone-800">Type / Status</th>
                                            <th className="p-4 font-semibold border-b border-stone-800">Source</th>
                                            <th className="p-4 font-semibold border-b border-stone-800 w-1/2">Content / Error</th>
                                            <th className="p-4 font-semibold border-b border-stone-800">ID</th>
                                            {['FAILED', 'PENDING', 'RUNNING'].includes(filterStatus) && <th className="p-4 font-semibold border-b border-stone-800">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-800/50">
                                        {inspectorItems.map((item) => (
                                            <tr key={item.id} className="hover:bg-stone-800/50 transition-colors group">
                                                <td className="p-4 text-sm text-stone-300">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${item.metadata?.status === 'FAILED' ? 'bg-red-900/30 text-red-400 border border-red-800' :
                                                        item.metadata?.status === 'COMPLETED' ? 'bg-green-900/30 text-green-400 border border-green-800' :
                                                            item.metadata?.status === 'COMPLETED_WITH_SKIPPED_CHUNKS' ? 'bg-orange-900/30 text-orange-400 border border-orange-800' :
                                                                item.metadata?.status === 'RUNNING' ? 'bg-blue-900/30 text-blue-400 border border-blue-800' :
                                                                    item.source_type === 'vector' ? 'bg-purple-900/30 text-purple-400' : 'bg-stone-800 text-stone-400'
                                                        }`}>
                                                        {item.metadata?.status === 'COMPLETED_WITH_SKIPPED_CHUNKS' ? 'PARTIAL SUCCESS' : (item.metadata?.status || item.metadata?.type || 'VECTOR')}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-sm text-white font-medium">
                                                    {item.metadata?.filename || item.metadata?.name || 'Unknown'}
                                                    <div className="text-xs text-stone-500 mt-1 flex items-center gap-2">
                                                        {item.metadata?.sku && <span className="bg-stone-800 px-1 rounded">{item.metadata.sku}</span>}
                                                        {item.metadata?.target && <span className="text-stone-600">{item.metadata.target}</span>}
                                                    </div>
                                                    {/* Audit Flags */}
                                                    <div className="flex gap-1 mt-1 flex-wrap">
                                                        {item.metadata?.flags?.ocr_complete && (
                                                            <span className="text-[10px] bg-blue-900/30 text-blue-300 px-1 rounded border border-blue-900">OCR: {item.metadata.flags.ocr_pages}p</span>
                                                        )}
                                                        {item.metadata?.flags?.image_context_required && (
                                                            <span className="text-[10px] bg-orange-900/30 text-orange-300 px-1 rounded border border-orange-900" title="Image content ignored (no text found)">⚠️ IMG CTX REQ</span>
                                                        )}
                                                        {item.metadata?.flags?.has_embedded_images && !item.metadata?.flags?.image_context_required && (
                                                            <span className="text-[10px] bg-green-900/30 text-green-300 px-1 rounded border border-green-900">IMG TEXT</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4 text-sm text-stone-400 font-mono text-xs break-all">
                                                    {item.content}
                                                </td>
                                                <td className="p-4 text-xs text-stone-600 font-mono">
                                                    {item.id}
                                                </td>
                                                {['FAILED', 'PENDING', 'RUNNING'].includes(filterStatus) && (
                                                    <td className="p-4 text-center flex gap-2 justify-center">
                                                        <button
                                                            onClick={() => handleRetry(item.id)}
                                                            title="Retry / Force Sync"
                                                            className="p-1 rounded bg-stone-800 hover:bg-stone-700 text-stone-400 hover:text-white transition-colors"
                                                        >
                                                            <RefreshCw size={14} />
                                                        </button>

                                                        {item.metadata?.flags?.image_context_required && (
                                                            <button
                                                                onClick={() => handleReview(item)}
                                                                title="Review Image Content"
                                                                className="p-1 rounded bg-orange-900/30 hover:bg-orange-800 text-orange-400 border border-orange-800 transition-colors"
                                                            >
                                                                <Eye size={14} /> Review
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                        {inspectorItems.length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="p-8 text-center text-stone-500">
                                                    No items found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )
            }

            {/* Review Modal */}
            {
                showReviewModal && reviewItem && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                        <div className="bg-stone-900 border border-stone-800 rounded-xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl">
                            <div className="p-4 border-b border-stone-800 flex justify-between items-center bg-stone-950/50">
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <Eye className="text-orange-400" /> Vision Review
                                    <span className="text-sm font-normal text-stone-500 ml-2">{reviewItem.metadata.filename}</span>
                                </h3>
                                <button onClick={() => setShowReviewModal(false)} className="text-stone-400 hover:text-white">Close</button>
                            </div>

                            <div className="flex-1 flex overflow-hidden">
                                {/* Left: Image Preview */}
                                <div className="w-1/2 bg-black/50 p-4 flex items-center justify-center overflow-auto border-r border-stone-800 relative">
                                    {/* Simple Path URL Conversion */}
                                    <img
                                        src={reviewItem.metadata.file_path.replace('/mnt/pim_storage', '/uploads').replace('/home/ulrix/pim/uploads', '/uploads')}
                                        alt="Preview"
                                        className="max-w-full max-h-full object-contain"
                                        onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder_image_error.png'; }}
                                    />
                                    <div className="absolute top-4 left-4 bg-black/60 text-white px-2 py-1 rounded text-xs">
                                        Preview Mode
                                    </div>
                                </div>

                                {/* Right: Analysis & Input */}
                                <div className="w-1/2 flex flex-col p-6 overflow-y-auto">

                                    {/* AI Zone */}
                                    <div className="mb-6 bg-stone-800/30 rounded-lg border border-stone-700 p-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <h4 className="text-stone-300 font-semibold flex items-center gap-2">
                                                <Sparkles size={16} className="text-purple-400" /> AI Suggestion
                                            </h4>
                                            <button
                                                onClick={analyzeImage}
                                                disabled={isAnalyzing}
                                                className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded transition-colors disabled:opacity-50"
                                            >
                                                {isAnalyzing ? "Analyzing..." : "Generate Suggestion"}
                                            </button>
                                        </div>

                                        <div className="bg-stone-950 p-3 rounded font-mono text-sm text-stone-400 min-h-[150px] whitespace-pre-wrap border border-stone-800">
                                            {aiSuggestion || "Click 'Generate Suggestion' to analyze image content..."}
                                        </div>

                                        {aiSuggestion && !aiSuggestion.startsWith("Error") && (
                                            <button
                                                onClick={() => setManualDescription(aiSuggestion)}
                                                className="mt-2 text-xs text-purple-400 hover:text-purple-300 underline"
                                            >
                                                Use this suggestion
                                            </button>
                                        )}
                                    </div>

                                    {/* Admin Zone */}
                                    <div className="flex-1 flex flex-col">
                                        <h4 className="text-stone-300 font-semibold mb-2 flex items-center gap-2">
                                            <CheckCircle size={16} className="text-green-400" /> Verified Description
                                        </h4>
                                        <p className="text-xs text-stone-500 mb-2">Only content in this field will be saved to the Knowledge Base.</p>
                                        <textarea
                                            value={manualDescription}
                                            onChange={(e) => setManualDescription(e.target.value)}
                                            className="flex-1 bg-stone-800 border-stone-700 text-white p-3 rounded resize-none focus:outline-none focus:border-green-500"
                                            placeholder="Enter verified description here..."
                                        />

                                        <div className="mt-4 flex justify-end gap-3">
                                            <button
                                                onClick={() => setShowReviewModal(false)}
                                                className="px-4 py-2 text-stone-400 hover:text-white"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={saveManualIngest}
                                                disabled={isSavingManual || !manualDescription.trim()}
                                                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Save size={18} /> Save & Ingest
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            <ConfirmationModal
                isOpen={showClearConfirm}
                onClose={() => setShowClearConfirm(false)}
                onConfirm={confirmClearFailed}
                title="Delete Failed Jobs"
                message="Are you sure you want to delete all failed jobs? This action cannot be undone."
                confirmText="Delete All"
                isDestructive={true}
            />

            <ConfirmationModal
                isOpen={showSyncConfirm}
                onClose={() => setShowSyncConfirm(false)}
                onConfirm={confirmSync}
                title="Rebuild Search Index?"
                message="This will delete all existing vector embeddings and re-process all documents. This is a CPU-intensive background task."
                confirmText="Start Rebuild"
                isDestructive={false}
            />
        </div >
    );
};

export default RagStatusTab;
